/**
 * validators.ts — PER-STEP VALIDATED COMPLETION (ADR-107 §2).
 *
 * THE ONE IDEA IN THIS FILE
 * ─────────────────────────
 * A step is done when its EVIDENCE says so. Not when a flag says so.
 *
 * The sign-up SOW failed UAT six times because onboarding progress was tracked
 * by a coarse `setupState` plus a `lastObwStep` cursor. `signup-api` stamps
 * `setupState: awaiting_payment` at ACCOUNT CREATION — the instant the public
 * form is submitted, before the journey has run a single step — so that field
 * cannot tell a never-started account from a part-finished one. Both populations
 * carry the same value. A field two populations share cannot discriminate
 * between them, and every prior fix was an attempt to make it do so anyway.
 *
 * Here, "is this step done?" is a pure function of what the platform durably
 * knows: the answers the subscriber actually gave, plus the server-side facts
 * those answers were supposed to produce. Resume, first-sign-in landing and
 * completion all derive from these validators and from nothing else (ADR-107
 * §2.2).
 *
 * ⭐ THIS FILE IMPORTS NOTHING, AND THAT IS DELIBERATE. The two §9 forks
 * (portal-subscriber, portal-staff) carry DIFFERENT `JourneyId` / `ObwStepId`
 * unions — staff has `sow-complete` and `pa-pick-subscriber`, subscriber has
 * `brand` and `activation`. A validator module that imported either fork's
 * journey types could not be the same file in both, so the "mirror" would be two
 * files that merely resembled each other — which is how mirrors drift.
 *
 * Instead the journey-level functions take the step list and the required list as
 * ARGUMENTS. The file is then byte-identical in both forks, and
 * `obw-validator-parity.test.ts` asserts exactly that: a real byte comparison,
 * not a hopeful convention.
 */

/** The tiers a `subscribe` answer may legitimately carry. */
export const VALID_TIERS = new Set(['T1', 'T2', 'T3']);

/**
 * Coarse lifecycle states that mean "this account is at or past payment".
 *
 * ⚠ USED ONLY AS CORROBORATION FOR THE `payment` VALIDATOR, never as a progress
 * signal in its own right (ADR-107 §2.3). `awaiting_payment` is deliberately
 * ABSENT: it is the value `signup-api` stamps at CREATION, so treating it as
 * evidence of payment would be the original defect verbatim — the one that
 * routed a subscriber who had run no step straight to the Payment screen.
 */
export const PAST_PAYMENT_STATES = new Set([
  'setup_in_progress',
  'paid_pending_activation',
  'active',
  'complete',
]);

/**
 * Step ids whose completion is backed by SERVER-SIDE evidence rather than by the
 * user's own answers. Everything else is answers-backed (see `isStepComplete`).
 */
export const EVIDENCE_BACKED_STEPS = new Set([
  'domain-verify',
  'subscribe',
  'payment',
  'profile',
]);

/**
 * Everything the platform durably knows about one account's onboarding.
 *
 * Assembled by the MOUNT from `/portal/me` (severance: this module reads no API,
 * no session, no route) and merged with the answers collected in the current
 * session, so the engine can re-validate as the user progresses.
 */
export interface ObwEvidence {
  /** Per-step answers, keyed by stepId — `obwStepData` from the server, merged
   *  with anything captured in this session. */
  answers: Record<string, Record<string, unknown> | undefined>;
  /** `Account.tier` — the server's record of the chosen plan. */
  tier?: string | null;
  /** The coarse lifecycle marker. Corroboration only — see PAST_PAYMENT_STATES. */
  setupState?: string | null;
  /**
   * How many Domain rows the account owns. SERVER TRUTH — see `domain-verify`
   * below for why this one field may never be substituted by an answer.
   */
  domainCount: number;
  /** Does an Account record exist server-side? */
  provisioned: boolean;
  /**
   * ⭐ TRUE WHEN THIS JOURNEY HAS NO ACCOUNT BEHIND IT AT ALL — the admin-run,
   * account-less mounts (ADR-107 §2.5): the staff BOW/SOW forks and the
   * subscriber-portal BOW landing, all of which mount with `accountId: ''` and
   * PROVISION at their completion step.
   *
   * ⚠ THIS IS NOT `!provisioned`, AND THE DIFFERENCE IS THE WHOLE POINT.
   * `provisioned` is read from `/portal/me` and is legitimately `false` when that
   * read fails — "unreachable" and "no account" are the same bytes there, and
   * conflating them is what restarted paid accounts at Step 1. `accountless` is a
   * property of the MOUNT, known with certainty before any I/O: this journey was
   * opened with no account id, so there is no account to have read about.
   *
   * It exists because it changes what the `profile` step must collect — see the
   * validator below.
   */
  accountless?: boolean;
  /**
   * ADR-124 I2/I3 — the country → zone map, as SERVED (`GET /portal/zone-map`), supplied by the
   * MOUNT. Only an account-less journey consults it (see the `residency` validator): a country
   * the platform does not place cannot be minted, so it cannot be "done". Absent (the map did not
   * load) ⇒ nothing is placed ⇒ an account-less `residency` step cannot pass — fail-closed, the
   * same direction as every mint site's server-side check.
   */
  countryZones?: Readonly<Record<string, string>>;
}

function answersFor(ev: ObwEvidence, stepId: string): Record<string, unknown> {
  return ev.answers[stepId] ?? {};
}

function hasAnswers(ev: ObwEvidence, stepId: string): boolean {
  return Object.keys(answersFor(ev, stepId)).length > 0;
}

function nonEmptyString(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

/** Is this step actually done, on this evidence? */
export function isStepComplete(stepId: string, ev: ObwEvidence): boolean {
  switch (stepId) {
    // ⛔ SERVER TRUTH ONLY — AND THIS IS THE WHOLE POINT OF THE VALIDATOR MODEL.
    //
    // The visible half of the #313 defect was a subscriber dead-ending on the
    // Payment step. The SILENT half was that entering at Payment walked past
    // `domain-verify`, so the account finished setup owning ZERO Domain rows — a
    // subscription to nothing, with every flag reporting success.
    //
    // The captured `domainUrl` answer is what the user TYPED. The Domain row is
    // what the platform MINTED. Accepting the answer as proof of the row would
    // reproduce the silent half exactly: the validator would pass, the wizard
    // would advance, and the account would still own nothing. And the two come
    // apart in practice, not just in theory — the mint is a uniqueness-guarded
    // server write that legitimately FAILS with 409 when the hostname belongs to
    // someone else, which is precisely the case where the answer exists and the
    // row does not.
    case 'domain-verify':
      return ev.domainCount >= 1;

    // A valid tier, from either side. `Account.tier` is written by the
    // `subscribe` step's from-state-guarded transition; the answer is what the
    // subscriber picked this session. Either is real evidence of a choice; a
    // value outside the catalogue is not evidence of anything.
    case 'subscribe': {
      const answered = answersFor(ev, 'subscribe').selectedTier;
      return (
        (typeof ev.tier === 'string' && VALID_TIERS.has(ev.tier)) ||
        (typeof answered === 'string' && VALID_TIERS.has(answered))
      );
    }

    // A payment record was captured, or the account's lifecycle has already moved
    // past payment.
    //
    // ⚠ THE CARD IS A STUB TODAY (ADR-073 Am.1 — StepPayment writes nothing to a
    // PSP), so the honest durable evidence is the persisted ANSWER SET for this
    // step. That is a real captured artefact, not a flag: it exists only because
    // the subscriber completed the step AND the (now authenticated — ADR-107
    // §2.4) step write persisted it. When a real PSP lands, this validator gains
    // a payment-record check and every consumer inherits it for free, because
    // nothing else in the platform decides what "paid" means.
    case 'payment':
      return (
        hasAnswers(ev, 'payment') ||
        (typeof ev.setupState === 'string' && PAST_PAYMENT_STATES.has(ev.setupState))
      );

    // The profile step's required fields. Checked BY NAME rather than by "any
    // answers at all", because a step that was opened and left can leave an
    // empty-ish map behind, and a business profile with no business name is not a
    // completed profile.
    //
    // ⭐ ON AN ACCOUNT-LESS JOURNEY THE OWNER'S EMAIL IS REQUIRED TOO, AND THIS IS
    // THE ONE PLACE THAT CAN KNOW IT. Such a journey ends by PROVISIONING from
    // these answers: no email, no Cognito user, no activation link, no account. So
    // the email is required-for-completion by construction — and leaving it
    // optional here is what let a consultant walk five screens past it and meet it
    // as a dead end at the final step ("subscriber email missing — Please go back
    // and complete"). A requirement the last step enforces is a requirement the
    // journey discovered too late.
    //
    // ⚠ AND IT IS REQUIRED ONLY THERE, DELIBERATELY. On an account-bearing journey
    // the owner's email is already held by the platform — public sign-up captures
    // it at the form, before the wizard runs — so this field is a business CONTACT
    // address, genuinely optional. Gating it there would break a signed-off flow to
    // re-collect something already known.
    case 'profile': {
      const a = answersFor(ev, 'profile');
      if (!nonEmptyString(a.businessName)) return false;
      if (ev.accountless && !nonEmptyString(a.businessEmail)) return false;
      return true;
    }

    // ⭐⭐ ADR-124 D1/I3 — WHERE THE BUSINESS'S DATA LIVES, DECIDED BY ITS COUNTRY.
    //
    // ON AN ACCOUNT-BEARING JOURNEY THE ANSWER ALREADY EXISTS, AND IS IMMUTABLE. The account was
    // minted before this wizard could run — the public sign-up validates the country at its form
    // (signup-api refuses an unplaced or other-zone country), every staff mint validates at its
    // route — and D1 makes the home immutable once created: there is no "move account" (D9 is
    // operator-run migration). So the step is a read-only DISPLAY there, and the evidence that it
    // is done is SERVER TRUTH that the home exists: `provisioned` (an Account record answered
    // `/portal/me`, in the zone that holds it). Keying it on the stored `jurisdiction` instead
    // would route every account created before ADR-124 (9 of them on 30 September 2026, none with
    // a stored country) back into setup to answer a question whose answer can change nothing —
    // and keying it on nothing at all would be a vacuous gate (obw-validators.test.ts refuses one).
    //
    // ON AN ACCOUNT-LESS JOURNEY THE ANSWER IS THE MINT'S INPUT. These journeys (staff SOW/BOW, the
    // partner-run BOW landing) create the home at their final step, from the country confirmed
    // here — so it must be a country the platform places, on the SERVED map. Evidence is the
    // confirmed answer checked against that map, never a flag that says "answered".
    case 'residency': {
      if (!ev.accountless) return ev.provisioned;
      const code = answersFor(ev, 'residency').jurisdiction;
      return (
        typeof code === 'string' &&
        !!ev.countryZones &&
        Object.prototype.hasOwnProperty.call(ev.countryZones, code)
      );
    }

    // ⭐ EVERY OTHER STEP IS ANSWERS-BACKED, AND THAT IS NOT A FALL-OPEN.
    //
    // The steps above are the ones with a server-side artefact to check, and they
    // are the ones where a false pass costs something real (an account with no
    // domain, no plan, or no payment). The rest — welcome, persona, brand, the
    // partner-apply and BOW/B2S bookends — produce no server artefact of their
    // own, so the answers ARE the evidence; there is nothing else to consult.
    //
    // This branch is also what keeps the file forkable: a step id that exists in
    // only one fork's union (staff's `sow-complete`, subscriber's `activation`)
    // gets sensible semantics here without either fork needing its own copy. A
    // future step gains answers-semantics until someone gives it real evidence —
    // and if it needs real evidence, it belongs in the switch above.
    default:
      return hasAnswers(ev, stepId);
  }
}

/**
 * The first REQUIRED step whose validator does not pass, or undefined when every
 * one of them does.
 *
 * ⭐ THIS IS THE RESUME, THE ENTRY POINT AND THE DONE-GATE, ALL THREE. They used
 * to be three separate mechanisms — a `lastObwStep` cursor, a `setupState`-derived
 * `requiredAction` redirect, and a step-index comparison — that could and did
 * disagree with one another. Which is what a "loop" actually is: two mechanisms
 * each correctly doing something the other undoes. One function cannot disagree
 * with itself.
 *
 * `steps` is the journey's EFFECTIVE sequence (post-`skipSteps`), so a step the
 * mount elided is never named as the place to resume. Required steps are matched
 * against that sequence, in its order.
 */
export function firstIncompleteRequiredStep<T extends string>(
  steps: readonly T[],
  required: readonly T[],
  ev: ObwEvidence,
): T | undefined {
  const req = new Set<string>(required);
  for (const stepId of steps) {
    if (!req.has(stepId)) continue;
    if (!isStepComplete(stepId, ev)) return stepId;
  }
  return undefined;
}

/** Is every REQUIRED step validated? (ADR-107 §2.2 — completion.) */
export function isJourneyComplete<T extends string>(
  steps: readonly T[],
  required: readonly T[],
  ev: ObwEvidence,
): boolean {
  return firstIncompleteRequiredStep(steps, required, ev) === undefined;
}

/**
 * Per-step completion across a whole sequence — for progress display, and for
 * tests that want the full picture rather than only the first failure.
 */
export function stepCompletionMap<T extends string>(
  steps: readonly T[],
  ev: ObwEvidence,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const stepId of steps) out[stepId] = isStepComplete(stepId, ev);
  return out;
}
